import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import WarningIcon from '@mui/icons-material/Warning';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowRight from '@mui/icons-material/ArrowRight';

const FallRiskFactorsGuide = () => {
  return (
    <div style={{ marginTop: '30px', marginBottom: '40px' }}>
      <Typography variant="h5" component="h2" sx={{ marginBottom: '20px', color: '#2c3e50', fontWeight: 500 }}>
        Fall Risk Factors Guide
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '25px' }}>
        {/* Intrinsic Factors */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
          <Avatar sx={{ bgcolor: '#3498db', width: 40, height: 40 }}>
            <AccessibilityIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#3498db', marginBottom: '5px' }}>
              Intrinsic Factors
            </Typography>
            <Typography variant="body1" sx={{ color: '#555' }}>
              Physical and medical conditions that increase fall risk
            </Typography>
            <List dense sx={{ marginLeft: '10px' }}>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Age-related changes (65+ years)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Muscle weakness and gait disorders" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Vision and hearing impairments" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Chronic medical conditions" />
              </ListItem>
            </List>
          </Box>
        </Box>

        {/* Environmental Factors */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
          <Avatar sx={{ bgcolor: '#e74c3c', width: 40, height: 40 }}>
            <WarningIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#e74c3c', marginBottom: '5px' }}>
              Environmental Hazards
            </Typography>
            <Typography variant="body1" sx={{ color: '#555' }}>
              External conditions that contribute to fall risk
            </Typography>
            <List dense sx={{ marginLeft: '10px' }}>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Poor lighting and glare" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Cluttered walkways and tripping hazards" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Slippery or uneven surfaces" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Inadequate handrails and grab bars" />
              </ListItem>
            </List>
          </Box>
        </Box>

        {/* Medication Factors */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
          <Avatar sx={{ bgcolor: '#9b59b6', width: 40, height: 40 }}>
            <LocalHospitalIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#9b59b6', marginBottom: '5px' }}>
              Medication Risks
            </Typography>
            <Typography variant="body1" sx={{ color: '#555' }}>
              Medications that may increase fall risk
            </Typography>
            <List dense sx={{ marginLeft: '10px' }}>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Sedatives and sleep medications" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Antihypertensives (blood pressure medications)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Diuretics (water pills)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText primary="Polypharmacy (multiple medications)" />
              </ListItem>
            </List>
          </Box>
        </Box>

        {/* Assessment Tools */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
          <Avatar sx={{ bgcolor: '#2ecc71', width: 40, height: 40 }}>
            <AssignmentIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#2ecc71', marginBottom: '5px' }}>
              Risk Assessment Tools
            </Typography>
            <Typography variant="body1" sx={{ color: '#555' }}>
              Standardized tools for evaluating fall risk
            </Typography>
            <List dense sx={{ marginLeft: '10px' }}>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Morse Fall Scale" 
                  secondary="Evaluates fall history, secondary diagnosis, ambulatory aids, IV/heparin lock, gait, and mental status" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="STRATIFY Risk Assessment Tool" 
                  secondary="Assesses fall history, agitation, visual impairment, frequent toileting, and transfer/mobility" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ArrowRight fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Hendrich II Fall Risk Model" 
                  secondary="Evaluates confusion, depression, altered elimination, dizziness, gender, medications, and mobility" 
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default FallRiskFactorsGuide;
